import React, { Component } from 'react';
import { Auth } from 'aws-amplify';
import axios from 'axios';
import { Typography, withStyles, CircularProgress } from '@material-ui/core';
import { red } from '@material-ui/core/colors';
import { Link } from 'react-router-dom';
import ContentHeader from '../components/ContentHeader';
import {
    formatTicketType,
    formatDate,
    formatPrice
} from '../helpers/formatters';
import ViewTicketButtons from '../components/ViewTicketButtons';
import ButtonLink from '../components/ButtonLink';
import globalStyles from '../styles';

const styles = theme => ({
    root: {
        textAlign: 'left',
        background: '#fff',
        boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.21)',
        border: '0',
        maxWidth: '800px',
        margin: '40px auto',
        position: 'relative',
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            margin: '20px 15px'
        }
    },
    spinner: {
        marginLeft: 'calc(50% - 22px)',
        marginTop: '60px',
        marginBottom: '60px'
    },
    error: {
        margin: '20px 0',
        fontSize: '18px',
        color: red[500],
        textAlign: 'center',
        paddingBottom: '20px'
    },
    passengerInfo: {
        padding: '10px',
        marginBottom: '20px',
        textAlign: 'center'
    },
    link: {
        ...globalStyles.link,
        fontWeight: 'bold'
    },
    button: {
        margin: '15px 5px 0'
    }
});

class TicketDetail extends Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: true,
            error: false,
            ticket: null
        };
    }

    async componentDidMount() {
        try {
            const currentSession = await Auth.currentSession();
            const {
                match: {
                    params: { ticketId }
                }
            } = this.props;

            const headers = {
                Authorization: currentSession.getIdToken().getJwtToken()
            };

            const response = await axios.get(
                `${
                    process.env.REACT_APP_API_ENDPOINT
                }/customer-ticket/user-id/${
                    currentSession.getAccessToken().payload.sub
                }/${ticketId}?include_notifications=true`,
                {
                    headers
                }
            );

            this.setState({
                ticket: response.data,
                loading: false,
                error: false
            });
        } catch (e) {
            this.setState({
                loading: false,
                error: true
            });
        }
    }

    render() {
        const { ticket, error, loading } = this.state;
        const { classes } = this.props;

        if (loading) {
            return (
                <CircularProgress
                    id="loading-spinner"
                    className={classes.spinner}
                />
            );
        }

        return (
            <div className={classes.root}>
                <ContentHeader
                    text="Ticket details"
                    id="ticket-details-heading"
                />
                {error && (
                    <Typography className={classes.error}>
                        There was a problem retrieving your tickets
                    </Typography>
                )}
                {ticket && !error && (
                    <div className={classes.passengerInfo}>
                        <Typography variant="body2">
                            Passenger: {ticket.passenger.first_name}{' '}
                            {ticket.passenger.last_name}
                        </Typography>
                        <Typography variant="body2">
                            Ticket:{' '}
                            {formatTicketType(
                                ticket.service_name,
                                ticket.type,
                                ticket.sub_type,
                                ticket.travel_pattern,
                                ticket.opt
                            )}
                        </Typography>
                        {ticket.type !== 'advance' && (
                            <>
                                {'yr' in ticket && (
                                    <Typography variant="body2">
                                        Year group:{' '}
                                        <span
                                            style={{
                                                textTransform: 'capitalize'
                                            }}
                                        >
                                            {ticket.yr}
                                        </span>
                                    </Typography>
                                )}
                                {'zone' in ticket && (
                                    <Typography variant="body2">
                                        Zone: {ticket.zone}
                                    </Typography>
                                )}
                                {('zone_stop' in ticket ||
                                    'stop' in ticket) && (
                                    <Typography variant="body2">
                                        Stop: {ticket.zone_stop || ticket.stop}
                                    </Typography>
                                )}
                                {'start' in ticket && (
                                    <Typography variant="body2">
                                        Valid from: {formatDate(ticket.start)}
                                    </Typography>
                                )}
                                {'expiry' in ticket && (
                                    <Typography variant="body2">
                                        Valid until: {formatDate(ticket.expiry)}
                                    </Typography>
                                )}
                                <Typography variant="body2">
                                    Price:{' '}
                                    {formatPrice(
                                        'total_price' in ticket
                                            ? ticket.total_price
                                            : ticket.price,
                                        ticket.currency
                                    )}
                                    {ticket.voucher_id && <> (Discounted)</>}
                                </Typography>
                            </>
                        )}
                        {ticket.type === 'flexi' && (
                            <Typography
                                variant="body2"
                                style={{ marginTop: 10 }}
                            >
                                {ticket.trips - ticket.trips_taken} trips left -{' '}
                                <Link
                                    className={classes.link}
                                    to={`/top-up/${ticket.id}`}
                                >
                                    Top up
                                </Link>{' '}
                                -{' '}
                                <Link
                                    className={classes.link}
                                    to={`/trips/${ticket.id}`}
                                >
                                    View trips
                                </Link>
                            </Typography>
                        )}
                        {ticket.type === 'advance' && (
                            <>
                                <ButtonLink
                                    className={classes.button}
                                    variant="outlined"
                                    color="primary"
                                    to={`/ticket/${ticket.id}/bookings`}
                                >
                                    Bookings
                                </ButtonLink>
                                <ButtonLink
                                    className={classes.button}
                                    variant="outlined"
                                    color="primary"
                                    to={`/book/${ticket.id}`}
                                >
                                    Book trip
                                </ButtonLink>
                            </>
                        )}
                        <ViewTicketButtons
                            ticketId={ticket.id}
                            hasNotifications={ticket.has_boarding_notifications}
                        />
                    </div>
                )}
            </div>
        );
    }
}

export default withStyles(styles)(TicketDetail);
